import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Typography, Spin, Upload, message } from 'antd';
import {
	DownloadOutlined,
	UploadOutlined,
	CloseOutlined,
	FileImageOutlined,
	FilePdfOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { getSignedUrl } from 'store/commonSlice';
import { uploadProformaInvoice } from 'store/financeSlice';
import { uploadProformaInvoiceV2 } from 'store/financeSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import ProformaInvoiceDetailsFields, {
	PROFORMA_DATE_FORMAT,
	PROFORMA_API_DATE_FORMAT,
} from './ProformaInvoiceDetailsFields';

const { Text } = Typography;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
	}
	.ant-modal-body {
		padding: 24px;
		max-height: 80vh;
		overflow-y: auto;
	}
`;

const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
	margin: 0;
	font-size: 24px;
	font-weight: 600;
	color: #000;
`;

const ButtonContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 24px;
	padding-top: 24px;
	border-top: 1px solid #f0f0f0;
`;

const CancelButton = styled(Button)`
	border: 1px solid #143e60;
	color: #143e60;
	background: #ffffff;
	&:hover {
		border-color: #1a4d7a;
		color: #1a4d7a;
		background: #ffffff;
	}
`;

const DownloadButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

const GenerateButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

const ReuploadButton = styled(Button)`
	color: #ffffff;
	background: #143e60;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

// Reupload Modal Styled Components
const ReuploadStyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
	}
	.ant-modal-body {
		padding: 36px 24px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 31px;
	}
	.ant-modal-header {
		display: none;
	}
	.ant-modal-close {
		display: none;
	}
`;

const ReuploadModalTitle = styled.h2`
	margin: 0;
	font-size: 22px;
	font-weight: 600;
	color: #000;
	font-family: 'Inter', sans-serif;
`;

const ReuploadArea = styled.div`
	width: 348px;
	border: 1px dashed #c6c6c8;
	border-radius: 4px;
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`;

const ReuploadHint = styled.p`
	margin: 0;
	font-size: 12px;
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	color: #99999b;
	text-align: center;
	letter-spacing: -0.36px;
	width: 100%;
`;

const ReuploadUploadButton = styled(Button)`
	height: 40px;
	padding: 8px 16px;
	border: 1px solid #c9c9c9;
	border-radius: 4px;
	background: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #18405d;
	}

	&:hover {
		border-color: #18405d;
		background: #ffffff;
	}
`;

const ReuploadUploadIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;

	svg {
		width: 20px;
		height: 20px;
		color: #18405d;
	}
`;

const ReuploadFilePreview = styled.div`
	width: 346px;
	height: 64px;
	padding: 8px 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const ReuploadFileInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
`;

const ReuploadFileIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;

	svg {
		width: 24px;
		height: 24px;
	}
`;

const ReuploadFileName = styled.span`
	font-size: 14px;
	font-weight: 500;
	font-family: 'Inter', sans-serif;
	color: #5dc1ff;
`;

const ReuploadRemoveButton = styled.button`
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;

	svg {
		width: 24px;
		height: 24px;
		color: #000;
	}

	&:hover svg {
		color: #ff4d4f;
	}
`;

const ReuploadButtonContainer = styled.div`
	display: flex;
	gap: 16px;
`;

const ReuploadCancelButton = styled(Button)`
	width: 136px;
	height: 40px;
	padding: 8px 16px;
	border: 1px solid #143e60;
	border-radius: 4px;
	background: #ffffff;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #143e60;
	}

	&:hover {
		border-color: #1a4d7a;
		background: #ffffff;
	}
`;

const ReuploadSubmitButton = styled(Button)`
	width: 136px;
	height: 40px;
	padding: 8px 16px;
	border: none;
	border-radius: 4px;
	background: #18405d;

	span {
		font-size: 16px;
		font-weight: 500;
		font-family: 'Inter', sans-serif;
		color: #ffffff;
	}

	&:hover {
		background: #1a4d7a;
	}

	&:disabled {
		background: #d9d9d9;
		color: #8c8c8c;
	}
`;

const ReuploadLoadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 31px;
	padding: 40px 0;
`;

const ReuploadLoadingText = styled.p`
	margin: 0;
	font-size: 16px;
	font-weight: 500;
	font-family: 'Inter', sans-serif;
	color: #000;
`;

const SpinnerWrapper = styled.div`
	width: 91px;
	height: 91px;
	position: relative;
`;

const Spinner = styled.svg`
	width: 100%;
	height: 100%;
	animation: rotate 1s linear infinite;

	@keyframes rotate {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
`;

// Upload icon SVG path
const uploadIconPath = (
	<>
		<path
			d="M12 16V4M12 4L8 8M12 4L16 8"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M3 17V18C3 19.6569 4.34315 21 6 21H18C19.6569 21 21 19.6569 21 18V17"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</>
);

// Helper function to get file icon based on extension for reupload modal
const getReuploadFileIcon = (filename) => {
	const extension = filename?.split('.').pop()?.toLowerCase();
	if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
		return <FileImageOutlined style={{ color: '#5dc1ff' }} />;
	}
	return <FilePdfOutlined style={{ color: '#5dc1ff' }} />;
};

const EmptyStateContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	gap: 24px;
`;

const EmptyIcon = styled.div`
	width: 120px;
	height: 120px;
	display: flex;
	align-items: center;
	justify-content: center;
	svg {
		width: 100%;
		height: 100%;
		opacity: 0.3;
	}
`;

const EmptyText = styled.div`
	font-size: 18px;
	font-weight: 600;
	color: #000;
`;

const InvoiceInfoContainer = styled.div`
	margin-bottom: 16px;
	padding: 12px;
	background: #f5f5f5;
	border-radius: 4px;
`;

const InfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
	&:last-child {
		margin-bottom: 0;
	}
`;

const InfoLabel = styled(Text)`
	font-weight: 500;
	color: #595959;
`;

const InfoValue = styled(Text)`
	color: #000;
	font-weight: 400;
`;

const IframeContainer = styled.div`
	margin: 16px 0;
	position: relative;
	min-height: 600px;
`;

const IframeFallback = styled.div`
	text-align: center;
	padding: 40px 20px;
	background: #f9f9f9;
	border: 1px solid #d9d9d9;
	border-radius: 6px;
`;

// Helper function to extract filename from S3 URL
const getFilenameFromUrl = (url) => {
	if (!url) return '';
	return url.startsWith('s3://') ? url.replace('s3://logitrack/', '') : url;
};

// Helper function to get file extension
const getFileExtension = (filename) => {
	if (!filename) return '';
	const parts = filename.split('.');
	if (parts.length < 2) return '';
	return parts.pop().toLowerCase();
};

// Common style for image container
const imageContainerStyle = {
	border: '1px solid #d9d9d9',
	borderRadius: '6px',
	padding: '20px',
	background: '#fff',
};

function ProFormaInvoiceModal({
	visible,
	onClose,
	record,
	onGenerate,
	onDownload,
	onReuploadSuccess,
}) {
	const dispatch = useDispatch();
	const isV2 = useMemo(() => getUIVersion() === 'new', []);
	const [generatedInvoice, setGeneratedInvoice] = useState(null);
	const [generating, setGenerating] = useState(false);
	const [viewUrl, setViewUrl] = useState(null);
	const [loadingUrl, setLoadingUrl] = useState(false);
	const [reuploadModalVisible, setReuploadModalVisible] = useState(false);
	const [reuploadFile, setReuploadFile] = useState(null);
	const [reuploadInvoiceNumber, setReuploadInvoiceNumber] = useState('');
	const [reuploadInvoiceDate, setReuploadInvoiceDate] = useState(null);
	const [reuploading, setReuploading] = useState(false);

	// Reset all state when modal opens
	useEffect(() => {
		if (visible) {
			setGeneratedInvoice(null);
			setViewUrl(null);
			setLoadingUrl(false);
			setReuploadModalVisible(false);
			setReuploadFile(null);
			setReuploadInvoiceNumber('');
			setReuploadInvoiceDate(null);
			setReuploading(false);
		}
	}, [visible]);

	// Helper function to construct filename from invoice number
	const constructFilenameFromInvoiceNumber = useCallback((invoiceNumber) => {
		if (!invoiceNumber) return null;
		// If invoice number doesn't already have extension, add .pdf
		return invoiceNumber.includes('.') ? invoiceNumber : `${invoiceNumber}.pdf`;
	}, []);

	// Helper function to extract filename from record
	const extractFilename = useCallback(
		(currentInvoice) => {
			if (isV2) {
				// V2: use vendor_proforma_invoice_key (direct S3 key)
				if (currentInvoice?.vendor_proforma_invoice_key) {
					return currentInvoice.vendor_proforma_invoice_key;
				}

				// Fallback 1: try to extract from URL if key is not available
				if (currentInvoice?.vendor_proforma_invoice_url) {
					return getFilenameFromUrl(currentInvoice.vendor_proforma_invoice_url);
				}

				// Fallback 2: construct filename from invoice number
				if (currentInvoice?.vendor_proforma_invoice_number) {
					const filename = constructFilenameFromInvoiceNumber(
						currentInvoice.vendor_proforma_invoice_number
					);
					console.log(
						'⚠️ [ProFormaInvoiceModal] Constructing filename from invoice number:',
						filename
					);
					return filename;
				}
			} else {
				// V1: original logic — requires vendor_proforma_invoice_url
				if (currentInvoice?.vendor_proforma_invoice_url) {
					return getFilenameFromUrl(currentInvoice.vendor_proforma_invoice_url);
				}

				// Fallback: construct from invoice number
				if (currentInvoice?.vendor_proforma_invoice_number) {
					const filename = constructFilenameFromInvoiceNumber(
						currentInvoice.vendor_proforma_invoice_number
					);
					console.log(
						'⚠️ [ProFormaInvoiceModal] V1: Constructing filename from invoice number:',
						filename
					);
					return filename;
				}
			}

			return null;
		},
		[isV2, constructFilenameFromInvoiceNumber]
	);

	// Helper function to extract signed URL from API response
	const extractSignedUrlFromResponse = useCallback((response) => {
		if (!response) return null;

		// Structure 1: { data: [{ url: "..." }] } - direct API response
		if (Array.isArray(response.data) && response.data[0]?.url) {
			return response.data[0].url;
		}

		// Structure 2: { data: { data: [{ url: "..." }] } } - wrapped response
		if (
			response.data?.data &&
			Array.isArray(response.data.data) &&
			response.data.data[0]?.url
		) {
			return response.data.data[0].url;
		}

		// Structure 3: response is the data array directly
		if (Array.isArray(response) && response[0]?.url) {
			return response[0].url;
		}

		// Structure 4: response has a direct url property
		if (response.url) {
			return response.url;
		}

		return null;
	}, []);

	// Function to fetch signed URL for proforma invoice
	const fetchSignedUrl = useCallback(() => {
		const currentInvoice = generatedInvoice || record;

		console.log('🔍 [ProFormaInvoiceModal] fetchSignedUrl called', {
			isV2,
			currentInvoice,
			vendor_proforma_invoice_number: currentInvoice?.vendor_proforma_invoice_number,
			vendor_proforma_invoice_key: currentInvoice?.vendor_proforma_invoice_key,
			vendor_proforma_invoice_url: currentInvoice?.vendor_proforma_invoice_url,
		});

		if (!currentInvoice?.vendor_proforma_invoice_number) {
			console.log('❌ [ProFormaInvoiceModal] No invoice number, skipping API call');
			return;
		}

		const filename = extractFilename(currentInvoice);

		if (!filename) {
			console.error('❌ [ProFormaInvoiceModal] No filename found, cannot fetch signed URL', {
				isV2,
				record: currentInvoice,
			});
			return;
		}

		console.log(
			'✅ [ProFormaInvoiceModal] Calling get-signed-url API with filename:',
			filename
		);

		setLoadingUrl(true);

		const data = {
			url_type: 'view',
			data: {
				entity: 'trip',
				keys: [filename],
			},
		};

		console.log('📤 [ProFormaInvoiceModal] API request data:', data);

		const signedUrlAction = getSignedUrl;
		dispatch(signedUrlAction(data))
			.then((response) => {
				console.log('📥 [ProFormaInvoiceModal] API response:', response);

				const signedUrl = extractSignedUrlFromResponse(response);

				if (signedUrl) {
					setViewUrl(signedUrl);
					console.log(
						'✅ [ProFormaInvoiceModal] Signed URL set successfully:',
						`${signedUrl.substring(0, 50)}...`
					);
				} else {
					console.error('❌ [ProFormaInvoiceModal] No signed URL in response:', response);
					message.error('Failed to load invoice preview: No URL in response');
				}
			})
			.catch((error) => {
				console.error('❌ [ProFormaInvoiceModal] Failed to get signed URL:', error);
				message.error('Failed to load invoice preview');
			})
			.finally(() => {
				setLoadingUrl(false);
			});
	}, [generatedInvoice, record, dispatch, isV2, extractFilename, extractSignedUrlFromResponse]);

	// Fetch signed URL when modal opens with valid invoice
	useEffect(() => {
		if (!visible) return;
		fetchSignedUrl();
	}, [visible, fetchSignedUrl]);

	const handleDownload = () => {
		const currentInvoice = generatedInvoice || record;
		if (onDownload && currentInvoice) {
			onDownload(currentInvoice);
		}
	};

	// Reupload modal handlers
	const handleReuploadClick = () => {
		// A re-upload replaces the number/date along with the file, so start from the saved ones.
		setReuploadInvoiceNumber(record?.proforma_uploaded_invoice_number || '');
		setReuploadInvoiceDate(
			record?.proforma_uploaded_invoice_date
				? moment(record.proforma_uploaded_invoice_date, PROFORMA_API_DATE_FORMAT)
				: null
		);
		setReuploadModalVisible(true);
	};

	const handleReuploadModalClose = useCallback(() => {
		setReuploadFile(null);
		setReuploadInvoiceNumber('');
		setReuploadInvoiceDate(null);
		setReuploading(false);
		setReuploadModalVisible(false);
	}, []);

	const handleReuploadBeforeUpload = useCallback((selectedFile) => {
		// Validate file type
		const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
		if (!allowedTypes.includes(selectedFile.type)) {
			message.error('Please upload JPEG, PNG or PDF file only');
			return false;
		}

		// Validate file size (50MB max)
		const maxSize = 50 * 1024 * 1024; // 50MB in bytes
		if (selectedFile.size > maxSize) {
			message.error('File size should not exceed 50MB');
			return false;
		}

		setReuploadFile(selectedFile);
		return false; // Prevent auto upload
	}, []);

	const handleReuploadRemoveFile = useCallback(() => {
		setReuploadFile(null);
	}, []);

	const handleReuploadSubmit = useCallback(async () => {
		if (!reuploadFile) {
			message.error('Please select a file to upload');
			return;
		}

		if (!record?.id) {
			message.error('Invalid record');
			return;
		}

		setReuploading(true);

		try {
			const uploadAction = isV2 ? uploadProformaInvoiceV2 : uploadProformaInvoice;
			const result = await dispatch(
				uploadAction({
					file: reuploadFile,
					id: record.id,
					invoiceNumber: reuploadInvoiceNumber,
					invoiceDate: reuploadInvoiceDate
						? reuploadInvoiceDate.format(PROFORMA_API_DATE_FORMAT)
						: null,
				})
			);

			if (result) {
				message.success('Pro-forma invoice re-uploaded successfully');
				if (onReuploadSuccess) {
					onReuploadSuccess(result);
				}
				handleReuploadModalClose();
				onClose(); // Close the main modal as well
			}
		} catch (error) {
			message.error(error?.message || 'Failed to re-upload pro-forma invoice');
		} finally {
			setReuploading(false);
		}
	}, [
		reuploadFile,
		reuploadInvoiceNumber,
		reuploadInvoiceDate,
		record,
		dispatch,
		onReuploadSuccess,
		handleReuploadModalClose,
		onClose,
		isV2,
	]);

	// Check if proforma_source is "uploaded"
	const isProformaUploaded = record?.proforma_source === 'uploaded';

	const handleGenerateClick = () => {
		if (!onGenerate || !record) return;

		setGenerating(true);
		onGenerate(record)
			.then((result) => {
				if (result) {
					setGeneratedInvoice({
						vendor_proforma_invoice_number:
							result.proforma_invoice_number || result.invoice_number,
						vendor_proforma_invoice_url: result.pdf_url || result.document_url,
					});
				}
			})
			.finally(() => {
				setGenerating(false);
			});
	};

	const renderEmptyState = () => (
		<EmptyStateContainer>
			<EmptyIcon>
				<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M14 2V8H20"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</EmptyIcon>
			<EmptyText>No Pro-Forma Found</EmptyText>
			<ButtonContainer>
				<CancelButton onClick={onClose}>Cancel</CancelButton>
				<GenerateButton onClick={handleGenerateClick} loading={generating}>
					Generate Pro-Forma
				</GenerateButton>
			</ButtonContainer>
		</EmptyStateContainer>
	);

	const renderInvoiceContent = () => {
		const currentInvoice = generatedInvoice || record;

		if (!currentInvoice?.vendor_proforma_invoice_number) {
			return renderEmptyState();
		}

		return (
			<>
				{currentInvoice.vendor_proforma_invoice_number && (
					<InvoiceInfoContainer>
						<InfoRow>
							<InfoLabel>Pro-Forma Invoice Number:</InfoLabel>
							<InfoValue>{currentInvoice.vendor_proforma_invoice_number}</InfoValue>
						</InfoRow>
						{currentInvoice.proforma_uploaded_invoice_number && (
							<InfoRow>
								<InfoLabel>Invoice Number:</InfoLabel>
								<InfoValue>
									{currentInvoice.proforma_uploaded_invoice_number}
								</InfoValue>
							</InfoRow>
						)}
						{currentInvoice.proforma_uploaded_invoice_date && (
							<InfoRow>
								<InfoLabel>Invoice Date:</InfoLabel>
								<InfoValue>
									{moment(
										currentInvoice.proforma_uploaded_invoice_date,
										PROFORMA_API_DATE_FORMAT
									).format(PROFORMA_DATE_FORMAT)}
								</InfoValue>
							</InfoRow>
						)}
					</InvoiceInfoContainer>
				)}

				<IframeContainer>
					{loadingUrl && (
						<div style={{ textAlign: 'center', padding: '60px 20px' }}>
							<Spin size="large" tip="Loading invoice..." />
						</div>
					)}
					{!loadingUrl &&
						viewUrl &&
						(() => {
							const filename = isV2
								? currentInvoice?.vendor_proforma_invoice_key
								: getFilenameFromUrl(currentInvoice?.vendor_proforma_invoice_url);
							const fileExt = getFileExtension(filename);
							const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);

							console.log('🖼️ [ProFormaInvoiceModal] Rendering preview', {
								filename,
								fileExt,
								isImage,
								viewUrl: `${viewUrl.substring(0, 50)}...`,
							});

							// Show image preview only if explicitly detected as image
							if (isImage) {
								return (
									<div style={{ textAlign: 'center', padding: '20px 0' }}>
										<div style={{ marginBottom: '20px' }}>
											<p
												style={{
													fontSize: '16px',
													marginBottom: '10px',
													fontWeight: 500,
												}}
											>
												Pro-Forma Invoice Preview
											</p>
										</div>
										<div style={imageContainerStyle}>
											<img
												src={viewUrl}
												alt="Pro-Forma Invoice"
												style={{
													maxWidth: '100%',
													maxHeight: '70vh',
													objectFit: 'contain',
												}}
												onError={(e) => {
													console.error(
														'❌ [ProFormaInvoiceModal] Image failed to load',
														e
													);
													e.target.style.display = 'none';
													const errorDiv = document.createElement('div');
													errorDiv.textContent =
														'Failed to load image preview';
													errorDiv.style.padding = '20px';
													errorDiv.style.color = '#999';
													e.target.parentElement.appendChild(errorDiv);
												}}
												onLoad={() => {
													console.log(
														'✅ [ProFormaInvoiceModal] Image loaded successfully'
													);
												}}
											/>
										</div>
									</div>
								);
							}

							// Default to PDF preview (pro-forma invoices are always PDFs)
							// This matches the old UI behavior
							return (
								<div style={{ textAlign: 'center', padding: '20px 0' }}>
									<div style={{ marginBottom: '20px' }}>
										<p
											style={{
												fontSize: '16px',
												marginBottom: '10px',
												fontWeight: 500,
											}}
										>
											Pro-Forma Invoice Preview
										</p>
										<p style={{ color: '#666', marginBottom: '20px' }}>
											For better PDF viewing experience, please open in a new
											tab
										</p>
									</div>
									<div
										style={{
											display: 'flex',
											gap: '10px',
											justifyContent: 'center',
											marginBottom: '20px',
										}}
									>
										<Button
											type="primary"
											onClick={() => window.open(viewUrl, '_blank')}
											size="large"
											style={{
												background: '#143e60',
												borderColor: '#143e60',
											}}
										>
											Open PDF in New Tab
										</Button>
									</div>
									{/* PDF iframe preview - same as old UI */}
									<div
										style={{
											border: '1px solid #d9d9d9',
											borderRadius: '6px',
										}}
									>
										<iframe
											title="Pro-Forma Invoice"
											src={`${viewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
											width="100%"
											height="400"
											style={{ border: 'none' }}
										/>
									</div>
								</div>
							);
						})()}
					{!loadingUrl && !viewUrl && (
						<IframeFallback>
							<Text type="secondary">Failed to load invoice preview</Text>
						</IframeFallback>
					)}
				</IframeContainer>

				<ButtonContainer>
					<CancelButton onClick={onClose}>Cancel</CancelButton>
					{isProformaUploaded && (
						<ReuploadButton icon={<UploadOutlined />} onClick={handleReuploadClick}>
							Reupload
						</ReuploadButton>
					)}
					<DownloadButton icon={<DownloadOutlined />} onClick={handleDownload}>
						Download
					</DownloadButton>
				</ButtonContainer>
			</>
		);
	};

	// Render Reupload Modal
	const renderReuploadModal = () => (
		<ReuploadStyledModal
			open={reuploadModalVisible}
			onCancel={reuploading ? undefined : handleReuploadModalClose}
			footer={null}
			width={420}
			centered
			destroyOnClose
			closable={!reuploading}
			maskClosable={!reuploading}
		>
			<ReuploadModalTitle>Reupload Pro-Forma</ReuploadModalTitle>

			{reuploading ? (
				<ReuploadLoadingContainer>
					<SpinnerWrapper>
						<Spinner viewBox="0 0 91 91" fill="none" xmlns="http://www.w3.org/2000/svg">
							<circle
								cx="45.5"
								cy="45.5"
								r="40"
								stroke="#E8E8E8"
								strokeWidth="8"
								fill="none"
							/>
							<path
								d="M45.5 5.5C67.591 5.5 85.5 23.409 85.5 45.5"
								stroke="#5DC1FF"
								strokeWidth="8"
								strokeLinecap="round"
								fill="none"
							/>
						</Spinner>
					</SpinnerWrapper>
					<ReuploadLoadingText>Uploading Pro-Forma Invoice</ReuploadLoadingText>
				</ReuploadLoadingContainer>
			) : (
				<>
					<ReuploadArea>
						<ReuploadHint>JPEG, PNG and PDF, formats up to 50 MB</ReuploadHint>
						<Upload
							beforeUpload={handleReuploadBeforeUpload}
							showUploadList={false}
							accept=".jpg,.jpeg,.png,.pdf"
						>
							<ReuploadUploadButton>
								<ReuploadUploadIcon>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										{uploadIconPath}
									</svg>
								</ReuploadUploadIcon>
								<span>Upload File</span>
							</ReuploadUploadButton>
						</Upload>
					</ReuploadArea>

					{reuploadFile && (
						<ReuploadFilePreview>
							<ReuploadFileInfo>
								<ReuploadFileIcon>
									{getReuploadFileIcon(reuploadFile.name)}
								</ReuploadFileIcon>
								<ReuploadFileName>{reuploadFile.name}</ReuploadFileName>
							</ReuploadFileInfo>
							<ReuploadRemoveButton onClick={handleReuploadRemoveFile} type="button">
								<CloseOutlined />
							</ReuploadRemoveButton>
						</ReuploadFilePreview>
					)}

					{reuploadFile && isV2 && (
						<ProformaInvoiceDetailsFields
							idPrefix="reupload-proforma"
							invoiceNumber={reuploadInvoiceNumber}
							invoiceDate={reuploadInvoiceDate}
							onInvoiceNumberChange={setReuploadInvoiceNumber}
							onInvoiceDateChange={setReuploadInvoiceDate}
						/>
					)}

					<ReuploadButtonContainer>
						<ReuploadCancelButton onClick={handleReuploadModalClose}>
							<span>Cancel</span>
						</ReuploadCancelButton>
						<ReuploadSubmitButton
							onClick={handleReuploadSubmit}
							disabled={!reuploadFile}
						>
							<span>Upload</span>
						</ReuploadSubmitButton>
					</ReuploadButtonContainer>
				</>
			)}
		</ReuploadStyledModal>
	);

	const renderContent = () => {
		if (generating) {
			return (
				<div style={{ textAlign: 'center', padding: '60px 20px' }}>
					<Spin size="large" tip="Generating pro-forma invoice..." />
				</div>
			);
		}

		return renderInvoiceContent();
	};

	return (
		<>
			<StyledModal
				title={
					<ModalHeader>
						<ModalTitle>Pro-Forma</ModalTitle>
					</ModalHeader>
				}
				open={visible}
				onCancel={onClose}
				footer={null}
				width={900}
				centered
				destroyOnClose
			>
				{renderContent()}
			</StyledModal>
			{renderReuploadModal()}
		</>
	);
}

ProFormaInvoiceModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	record: PropTypes.object,
	onGenerate: PropTypes.func.isRequired,
	onDownload: PropTypes.func,
	onReuploadSuccess: PropTypes.func,
};

export default ProFormaInvoiceModal;
